/**
 * Renders a `:::stats` block from the auto-published blog markdown.
 * The voice prompt emits this block with exactly 4 LABEL | VALUE rows.
 */

interface Stat {
  label: string;
  value: string;
}

interface Props {
  stats: Stat[];
}

export function StatsBlock({ stats }: Props) {
  if (stats.length === 0) return null;
  return (
    <div className="not-prose grid grid-cols-2 md:grid-cols-4 gap-3 my-8">
      {stats.map((s, i) => (
        <div
          key={i}
          className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-xl px-4 py-5 text-center"
        >
          <div className="text-2xl md:text-3xl font-bold text-blue-700 leading-tight">
            {s.value}
          </div>
          <div className="text-xs uppercase tracking-wide text-blue-900/60 mt-2">
            {s.label}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Pull all `:::stats ... :::` blocks out of the markdown. Returns the cleaned
 * markdown (without the blocks) and the parsed stat rows in order. The slug
 * page renders the first stats block at the top and the rest are dropped —
 * the prompt only emits one, but we tolerate multiples.
 */
export function extractStatsBlocks(markdown: string): {
  cleaned: string;
  stats: Stat[];
} {
  const STATS_RE = /:::stats\s*\n([\s\S]*?)\n:::/g;
  const stats: Stat[] = [];
  let firstBlockOnly = true;

  const cleaned = markdown.replace(STATS_RE, (_match, body: string) => {
    if (!firstBlockOnly) return "";
    firstBlockOnly = false;
    body
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .forEach((line) => {
        const [label, value] = line.split("|").map((s) => s.trim());
        if (label && value) stats.push({ label, value });
      });
    return ""; // remove from markdown; component renders it separately
  });

  return { cleaned: cleaned.trim(), stats };
}
